import React, { useEffect, useState } from 'react';
import { Card, Button, Space, Typography, Divider, Tag, Modal, message } from 'antd';
import { UserOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, CrownOutlined } from '@ant-design/icons';
import SingleFieldGroupForm from './SingleFieldGroupForm';
import { useGetFieldConfigurationForCategoryGrouped } from '@/shared/api/client/field-configuration-controller';
import { FieldGroupDTO } from '@/shared/api/model';

const { Title, Text } = Typography;

interface GroupMember {
  id: string;
  isLeader: boolean;
  customerData: Record<string, any>;
  role?: string;
}

interface GroupMemberFormProps {
  member: GroupMember;
  memberIndex: number;
  memberTemplateId: number; // The category ID to use for member fields
  onUpdate: (memberIndex: number, data: Record<string, any>) => void;
  onRemove: (memberIndex: number) => void;
  onSetLeader: (memberIndex: number) => void;
  canRemove: boolean;
  readonly?: boolean;
}

const GroupMemberForm: React.FC<GroupMemberFormProps> = ({
  member,
  memberIndex,
  memberTemplateId,
  onUpdate,
  onRemove,
  onSetLeader,
  canRemove,
  readonly = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localData, setLocalData] = useState<Record<string, any>>(member.customerData || {});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch field configuration for member template
  const {
    data: fieldConfiguration,
    isLoading: fieldsLoading,
    error: fieldsError,
  } = useGetFieldConfigurationForCategoryGrouped(
    memberTemplateId,
    {
      query: {
        enabled: !!memberTemplateId && isExpanded,
      },
    }
  );

  useEffect(() => {
    setLocalData(member.customerData || {});
  }, [member.customerData]);

  const handleFieldChange = (fieldName: string, value: any) => {
    const updatedData = {
      ...localData,
      [fieldName]: value,
    };
    setLocalData(updatedData);
    onUpdate(memberIndex, updatedData);
  };

  const handleRemove = () => {
    Modal.confirm({
      title: 'Remove Member',
      content: `Are you sure you want to remove ${member.isLeader ? 'the group leader' : 'this member'}?`,
      okText: 'Remove',
      okType: 'danger',
      onOk: () => {
        onRemove(memberIndex);
        message.success('Member removed successfully');
      },
    });
  };

  const handleSetLeader = () => {
    Modal.confirm({
      title: 'Set as Group Leader',
      content: 'Are you sure you want to set this member as the group leader?',
      okText: 'Confirm',
      onOk: () => {
        onSetLeader(memberIndex);
        message.success('Group leader updated successfully');
      },
    });
  };

  // Calculate completion percentage
  const getCompletionPercentage = (): number => {
    if (!fieldConfiguration?.fieldGroups) return 0;
    
    const allFields = fieldConfiguration.fieldGroups.flatMap(group => group.fields || []);
    const requiredFields = allFields.filter(field => field.defaultRequired);
    
    if (requiredFields.length === 0) return 100;
    
    const filledFields = requiredFields.filter(field => {
      if (!field.fieldName) return false;
      const value = localData[field.fieldName];
      return value !== undefined && value !== null && value !== '';
    });
    
    return Math.round((filledFields.length / requiredFields.length) * 100);
  };

  const completionPercentage = getCompletionPercentage();
  const isComplete = completionPercentage === 100;

  // Get member display name
  const getMemberName = (): string => {
    const name = localData.name || localData.firstName || localData.fullName;
    return name || `Member ${memberIndex + 1}`;
  };

  return (
    <Card
      className="group-member-card"
      style={{ marginBottom: 16 }}
      styles={{
        body: { padding: isExpanded ? 24 : 16 }
      }}
    >
      {/* Member Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space size="middle">
          <UserOutlined style={{ fontSize: 24, color: member.isLeader ? '#faad14' : '#1890ff' }} />
          <div>
            <Space>
              <Title level={5} style={{ margin: 0 }}>
                {getMemberName()}
              </Title>
              {member.isLeader && <Tag color="gold" icon={<CrownOutlined />}>Leader</Tag>}
              {isComplete && <Tag color="success" icon={<CheckCircleOutlined />}>Complete</Tag>}
            </Space>
            <Text type="secondary">
              {member.role || 'Member'} • {completionPercentage}% Complete
            </Text>
          </div>
        </Space>

        <Space>
          <Button
            type={isExpanded ? 'default' : 'primary'}
            icon={<EditOutlined />}
            onClick={() => setIsExpanded(!isExpanded)}
            disabled={readonly}
          >
            {isExpanded ? 'Collapse' : 'Edit'}
          </Button>
          
          {!member.isLeader && (
            <Button
              icon={<CrownOutlined />}
              onClick={handleSetLeader}
              disabled={readonly}
            >
              Set as Leader
            </Button>
          )}
          
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleRemove}
            disabled={!canRemove || readonly}
          >
            Remove
          </Button>
        </Space>
      </div>

      {/* Member Form Fields */}
      {isExpanded && (
        <>
          <Divider />
          
          {fieldsLoading && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Text type="secondary">Loading member form...</Text>
            </div>
          )}

          {fieldsError && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Text type="danger">Failed to load member form configuration</Text>
            </div>
          )}

          {fieldConfiguration?.fieldGroups && (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {fieldConfiguration.fieldGroups
                .filter((group: FieldGroupDTO) => group.isVisible)
                .sort((a: FieldGroupDTO, b: FieldGroupDTO) => (a.displayOrder || 0) - (b.displayOrder || 0))
                .map((group: FieldGroupDTO) => (
                  <div key={group.groupKey}>
                    <Title level={5}>{group.groupName}</Title>
                    {group.description && (
                      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                        {group.description}
                      </Text>
                    )}
                    
                    <SingleFieldGroupForm
                      fieldGroup={group}
                      formData={localData}
                      onFieldChange={handleFieldChange}
                      errors={errors}
                      readonly={readonly}
                    />
                  </div>
                ))}
            </Space>
          )}
        </>
      )}
    </Card>
  );
};

export default GroupMemberForm;
